import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import Button from './Button'

/**
 * ImageUploader — generic upload-with-preview-and-delete component.
 *
 * Props:
 *   currentUrl      — string | null         current saved image URL
 *   onUpload        — async (file) => {success, imageUrl, message}
 *   onDelete        — async () => {success, message}
 *   onChange        — (newUrl: string|null) => void   called after success
 *   maxBytes        — defaults to 5 MB
 *   acceptTypes     — defaults to PNG/JPG/GIF/WebP
 *   imageClassName  — Tailwind classes applied to the preview <img>
 *   layout          — 'block' (default, full width) or 'compact' (smaller)
 *
 * Behavior: pick a file → see preview → click Save (uploads) or Cancel.
 * If a saved image is already there, you can Replace or Remove.
 */
export default function ImageUploader({
    currentUrl,
    onUpload,
    onDelete,
    onChange,
    maxBytes = 5 * 1024 * 1024,
    acceptTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
    imageClassName = 'max-h-48 max-w-full',
    layout = 'block',
}) {
    const fileInputRef = useRef(null)
    const [pendingFile, setPendingFile] = useState(null)
    const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null)
    const [busy, setBusy] = useState(false)

    // Clean up object URLs to avoid leaking memory.
    useEffect(() => {
        return () => {
            if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
        }
    }, [pendingPreviewUrl])

    function handleFilePick(e) {
        const file = e.target.files?.[0]
        if (!file) return

        if (!acceptTypes.includes(file.type)) {
            toast.error('Unsupported file type')
            return
        }
        if (file.size > maxBytes) {
            toast.error(`Image must be smaller than ${Math.round(maxBytes / 1024 / 1024)} MB`)
            return
        }

        if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
        setPendingFile(file)
        setPendingPreviewUrl(URL.createObjectURL(file))
    }

    function clearPending() {
        if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl)
        setPendingFile(null)
        setPendingPreviewUrl(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    async function handleSave() {
        if (!pendingFile) return
        setBusy(true)
        try {
            const response = await onUpload(pendingFile)
            if (response?.success) {
                toast.success('Image saved')
                onChange?.(response.imageUrl ?? response.logoUrl ?? null)
                clearPending()
            } else {
                toast.error(response?.message || 'Upload failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    async function handleDelete() {
        if (!confirm('Remove this image?')) return
        setBusy(true)
        try {
            const response = await onDelete()
            if (response?.success) {
                toast.success('Image removed')
                onChange?.(null)
            } else {
                toast.error(response?.message || 'Delete failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    // ----- Pending file (preview before upload) -----------------------------
    if (pendingFile) {
        return (
            <div className="space-y-2">
                <div className="text-xs text-gray-500">Preview:</div>
                <div className="flex items-center justify-center bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <img src={pendingPreviewUrl} alt="Pending preview" className={imageClassName} />
                </div>
                <div className="text-xs text-gray-500">
                    {pendingFile.name} — {(pendingFile.size / 1024).toFixed(1)} KB
                </div>
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="secondary" onClick={clearPending} disabled={busy}>Cancel</Button>
                    <Button type="button" onClick={handleSave} loading={busy}>Save</Button>
                </div>
            </div>
        )
    }

    // ----- No pending file --------------------------------------------------
    const padding = layout === 'compact' ? 'p-3' : 'p-4'
    return (
        <div className="space-y-2">
            {currentUrl ? (
                <>
                    <div className={`flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200 ${padding}`}>
                        <img src={currentUrl} alt="Current" className={imageClassName} />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                            Replace
                        </Button>
                        <Button type="button" variant="danger" onClick={handleDelete} disabled={busy}>
                            Remove
                        </Button>
                    </div>
                </>
            ) : (
                <div className={`flex flex-col items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 ${layout === 'compact' ? 'p-4' : 'p-6'}`}>
                    <p className="text-xs text-gray-500 mb-2">No image</p>
                    <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={busy}>
                        Upload Image
                    </Button>
                </div>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept={acceptTypes.join(',')}
                onChange={handleFilePick}
                className="hidden"
            />
        </div>
    )
}